import { Box, IconSearch, Stack, TextField } from 'braid-design-system';
import {
  type GraphQLField,
  type GraphQLNamedType,
  OperationTypeNode,
  isEnumType,
  isInputObjectType,
  isInterfaceType,
  isObjectType,
  isScalarType,
  isUnionType,
} from 'graphql';
import {
  type ComponentProps,
  forwardRef,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { matchPath, useLocation } from 'react-router';

import { schema } from '../schema.ts';
import { webhookSchema } from '../webhookSchema.ts';

import * as styles from './SchemaMenu.css';
import {
  type NestedTypes,
  nestSubordinateTypes,
} from './nestSubordinateTypes.ts';

import { HighlightedText } from '#src/components/HighlightedText/HighlightedText.tsx';
import { MenuItem } from '#src/components/MenuItem/MenuItem.tsx';
import { MenuSection } from '#src/components/MenuSection/MenuSection.tsx';
import {
  hrefForNamedType,
  hrefForOperation,
  hrefForWebhookSchemaDefinition,
  hrefForWebhookSchemaRootType,
} from '#src/utils/href.ts';

const SchemaMenuItem = forwardRef<
  HTMLAnchorElement,
  Omit<
    ComponentProps<typeof MenuItem>,
    'isExpanded' | 'setUserExpanded' | 'children'
  > & {
    activePath: string;
    children: string;
    indent?: boolean;
    filter: string;
  }
>(({ activePath, to, children: text, indent, filter, ...props }, ref) => {
  const children = (
    <HighlightedText searchFilter={filter}>{text}</HighlightedText>
  );

  return (
    <MenuItem
      {...props}
      to={to}
      ref={to && matchPath(activePath, to) ? ref : null}
      isExpanded={false}
      setUserExpanded={() => {}}
    >
      {indent ? <Box paddingLeft="small">{children}</Box> : children}
    </MenuItem>
  );
});

const RootType = ({
  type,
  itemRef,
  activePath,
  nestedTypes,
  onClick,
  filter,
}: {
  type: GraphQLNamedType;
  itemRef: React.RefObject<HTMLAnchorElement | null>;
  activePath: string;
  nestedTypes: NestedTypes;
  onClick: () => void;
  filter: string;
}) => (
  <>
    <SchemaMenuItem
      ref={itemRef}
      key={type.name}
      to={hrefForNamedType(type)}
      activePath={activePath}
      onClick={onClick}
      filter={filter}
    >
      {type.name}
    </SchemaMenuItem>

    {nestedTypes.subordinateTypes
      .get(type.name)
      ?.map(({ type: subordinateType, label }) => (
        <SchemaMenuItem
          ref={itemRef}
          key={subordinateType.name}
          to={hrefForNamedType(subordinateType)}
          activePath={activePath}
          onClick={onClick}
          filter={filter}
          indent
        >
          {label}
        </SchemaMenuItem>
      ))}
  </>
);

const NamedTypesSection = ({
  rootTypes,
  heading,
  itemRef,
  activePath,
  nestedTypes,
  onClick,
  filter,
}: {
  rootTypes: GraphQLNamedType[];
  heading: string;
  itemRef: React.RefObject<HTMLAnchorElement | null>;
  activePath: string;
  nestedTypes: NestedTypes;
  onClick: () => void;
  filter: string;
}) => (
  <MenuSection heading={heading}>
    {rootTypes.map((type) => (
      <RootType
        key={`${type.name}-group`}
        type={type}
        itemRef={itemRef}
        activePath={activePath}
        nestedTypes={nestedTypes}
        onClick={onClick}
        filter={filter}
      />
    ))}
  </MenuSection>
);

const OperationFieldsSection = ({
  heading,
  fields,
  operationType,
  itemRef,
  activePath,
  onClick,
  filter,
}: {
  operationType: OperationTypeNode;
  heading: string;
  fields: Array<GraphQLField<unknown, unknown, unknown>>;
  itemRef: React.RefObject<HTMLAnchorElement | null>;
  activePath: string;
  onClick: () => void;
  filter: string;
}) => (
  <MenuSection heading={heading}>
    {fields.map((field) => (
      <SchemaMenuItem
        ref={itemRef}
        key={field.name}
        to={hrefForOperation(operationType, field)}
        activePath={activePath}
        onClick={onClick}
        filter={filter}
      >
        {field.name}
      </SchemaMenuItem>
    ))}
  </MenuSection>
);

const WebhookSchemaSection = ({
  defNames,
  heading,
  itemRef,
  activePath,
  onClick,
  filter,
}: {
  defNames: string[];
  heading: string;
  itemRef: React.RefObject<HTMLAnchorElement | null>;
  activePath: string;
  onClick: () => void;
  filter: string;
}) => (
  <MenuSection heading={heading}>
    {typeof webhookSchema.title === 'string' && (
      <SchemaMenuItem
        to={hrefForWebhookSchemaRootType()}
        ref={itemRef}
        activePath={activePath}
        onClick={onClick}
        filter={filter}
      >
        {webhookSchema.title}
      </SchemaMenuItem>
    )}

    {defNames.map((defName) => (
      <SchemaMenuItem
        key={defName}
        to={hrefForWebhookSchemaDefinition(defName)}
        ref={itemRef}
        activePath={activePath}
        onClick={onClick}
        filter={filter}
      >
        {defName}
      </SchemaMenuItem>
    ))}
  </MenuSection>
);

const includeOperation = (
  field: GraphQLField<unknown, unknown>,
  filter: string,
) =>
  !field.name.startsWith('_') &&
  typeof field.deprecationReason !== 'string' &&
  (filter.length === 0 || field.name.toLowerCase().includes(filter));

export const SchemaMenu = ({
  scrollingParentRef,
  onClick,
  isOpen,
}: {
  scrollingParentRef: React.RefObject<HTMLElement | null>;
  onClick: () => void;
  isOpen: boolean;
}) => {
  const { pathname } = useLocation();
  const itemRef = useRef<HTMLAnchorElement>(null);
  const [rawFilter, setFilter] = useState('');

  useEffect(() => {
    setFilter('');
  }, [isOpen]);

  const filter = rawFilter.toLowerCase().trim();

  const nestedTypes = useMemo(
    () => nestSubordinateTypes(schema.getTypeMap()),
    [],
  );

  const types = useMemo(() => {
    const filteredRootTypes = nestedTypes.rootTypes.filter((type) =>
      type.name.toLowerCase().includes(filter),
    );

    return {
      enumTypes: filteredRootTypes.filter(isEnumType),
      interfaceTypes: filteredRootTypes.filter(isInterfaceType),
      inputObjectTypes: filteredRootTypes.filter(isInputObjectType),
      unionTypes: filteredRootTypes.filter(isUnionType),
      scalarTypes: filteredRootTypes.filter(isScalarType),
      objectTypes: filteredRootTypes.filter(isObjectType),
      queries: Object.values(schema.getQueryType()?.getFields() ?? {}).filter(
        (query) => includeOperation(query, filter),
      ),
      mutations: Object.values(
        schema.getMutationType()?.getFields() ?? {},
      ).filter((mutation) => includeOperation(mutation, filter)),
    };
  }, [nestedTypes, filter]);

  // Make searching for "webhook" show the entire webhook schema
  const showAllWebhookDefs = 'webhook'.includes(filter.toLowerCase());

  const webhookSchemaDefNames =
    typeof webhookSchema === 'object'
      ? Object.keys(webhookSchema.definitions).filter(
          (defName) =>
            showAllWebhookDefs ||
            defName.toLowerCase().includes(filter.toLowerCase()),
        )
      : [];

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (pathname === '/schema') {
        scrollingParentRef.current?.scrollTo({
          top: 0,
          behavior: 'instant',
        });
      } else if (pathname.startsWith('/schema')) {
        itemRef.current?.scrollIntoView({
          behavior: 'instant',
          block: 'nearest',
        });
      }
    });

    return () => clearTimeout(timeout);
  }, [pathname, scrollingParentRef]);

  return (
    <Stack space="small">
      <Box paddingX="xsmall" display={['none', 'block']}>
        <TextField
          id="filter-schema"
          type="search"
          icon={<IconSearch />}
          aria-label="Filter schema"
          placeholder="Filter schema"
          value={rawFilter}
          onChange={(e) => setFilter(e.currentTarget.value)}
          onClear={() => setFilter('')}
        />
      </Box>

      <Box className={styles.scrollableMenuSection}>
        <Stack space="small">
          {rawFilter.length === 0 ? (
            <SchemaMenuItem
              to="/schema/definition"
              ref={itemRef}
              activePath={pathname}
              onClick={onClick}
              filter={filter}
            >
              Schema definition
            </SchemaMenuItem>
          ) : null}

          <Box paddingTop="small">
            <Stack space="large">
              {types.queries.length > 0 && (
                <OperationFieldsSection
                  operationType={OperationTypeNode.QUERY}
                  heading="Queries"
                  fields={types.queries}
                  itemRef={itemRef}
                  activePath={pathname}
                  onClick={onClick}
                  filter={filter}
                />
              )}

              {types.mutations.length > 0 && (
                <OperationFieldsSection
                  operationType={OperationTypeNode.MUTATION}
                  heading="Mutations"
                  fields={types.mutations}
                  itemRef={itemRef}
                  activePath={pathname}
                  onClick={onClick}
                  filter={filter}
                />
              )}

              {types.objectTypes.length > 0 && (
                <NamedTypesSection
                  rootTypes={types.objectTypes}
                  heading="Objects"
                  itemRef={itemRef}
                  activePath={pathname}
                  nestedTypes={nestedTypes}
                  onClick={onClick}
                  filter={filter}
                />
              )}

              {types.interfaceTypes.length > 0 && (
                <NamedTypesSection
                  rootTypes={types.interfaceTypes}
                  heading="Interfaces"
                  itemRef={itemRef}
                  activePath={pathname}
                  nestedTypes={nestedTypes}
                  onClick={onClick}
                  filter={filter}
                />
              )}

              {types.unionTypes.length > 0 && (
                <NamedTypesSection
                  rootTypes={types.unionTypes}
                  heading="Unions"
                  itemRef={itemRef}
                  activePath={pathname}
                  nestedTypes={nestedTypes}
                  onClick={onClick}
                  filter={filter}
                />
              )}

              {types.inputObjectTypes.length > 0 && (
                <NamedTypesSection
                  rootTypes={types.inputObjectTypes}
                  heading="Input objects"
                  itemRef={itemRef}
                  activePath={pathname}
                  nestedTypes={nestedTypes}
                  onClick={onClick}
                  filter={filter}
                />
              )}

              {types.enumTypes.length > 0 && (
                <NamedTypesSection
                  rootTypes={types.enumTypes}
                  heading="Enums"
                  itemRef={itemRef}
                  activePath={pathname}
                  nestedTypes={nestedTypes}
                  onClick={onClick}
                  filter={filter}
                />
              )}

              {types.scalarTypes.length > 0 && (
                <NamedTypesSection
                  rootTypes={types.scalarTypes}
                  heading="Scalars"
                  itemRef={itemRef}
                  activePath={pathname}
                  nestedTypes={nestedTypes}
                  onClick={onClick}
                  filter={filter}
                />
              )}

              {webhookSchemaDefNames.length > 0 && (
                <WebhookSchemaSection
                  defNames={webhookSchemaDefNames}
                  heading="Webhook JSON schema"
                  itemRef={itemRef}
                  activePath={pathname}
                  onClick={onClick}
                  filter={filter}
                />
              )}
            </Stack>
          </Box>
        </Stack>
      </Box>
    </Stack>
  );
};
